import { useMemo } from 'react'
import { useAccessContext } from '../context/AccessContext'
import { getIndustryPreset, type IndustryTerminology } from '../domain/industryPresets'
import type { AccessContext } from '../types/access'

export type IndustryLabelKey =
  | 'worker'
  | 'workers'
  | 'job'
  | 'jobs'
  | 'location'
  | 'locations'
  | 'shift'
  | 'shifts'
  | 'assignment'
  | 'assignments'
  | 'timesheet'
  | 'payroll'
  | 'compliance'

const terminologyKeyByLabel: Record<IndustryLabelKey, keyof IndustryTerminology> = {
  worker: 'workerLabel',
  workers: 'workersLabel',
  job: 'jobLabel',
  jobs: 'jobsLabel',
  location: 'locationLabel',
  locations: 'locationsLabel',
  shift: 'shiftLabel',
  shifts: 'shiftsLabel',
  assignment: 'assignmentLabel',
  assignments: 'assignmentsLabel',
  timesheet: 'timesheetLabel',
  payroll: 'payrollLabel',
  compliance: 'complianceLabel',
}

const generic = getIndustryPreset('general_workforce').terminology

const firstSlashSegment = (value: string) => value.split('/')[0]?.trim() || value

const singularFromPlural = (plural: string, genericSingular: string) => {
  const primary = firstSlashSegment(plural)
  if (!primary || primary === plural && plural === generic[`${genericSingular.toLowerCase()}sLabel` as keyof IndustryTerminology]) return genericSingular
  if (primary.endsWith('ies')) return `${primary.slice(0, -3)}y`
  if (primary.endsWith('s') && !primary.endsWith('ss')) return primary.slice(0, -1)
  return primary
}

export const resolveIndustryTerminology = (accessContext?: AccessContext | null) => {
  const industryType = accessContext?.industryType || accessContext?.tenant?.industry_type || accessContext?.tenant?.industryType
  const fallbackPreset = getIndustryPreset(industryType)
  const backendTerminology = (accessContext?.terminology || {}) as Partial<IndustryTerminology>
  const terminology: IndustryTerminology = {
    ...generic,
    ...fallbackPreset.terminology,
    ...backendTerminology,
  }

  if (terminology.workerLabel === generic.workerLabel && terminology.workersLabel !== generic.workersLabel) {
    terminology.workerLabel = singularFromPlural(terminology.workersLabel, generic.workerLabel)
  }
  if (terminology.jobLabel === generic.jobLabel && terminology.jobsLabel !== generic.jobsLabel) {
    terminology.jobLabel = singularFromPlural(terminology.jobsLabel, generic.jobLabel)
  }
  if (terminology.locationLabel === generic.locationLabel && terminology.locationsLabel !== generic.locationsLabel) {
    terminology.locationLabel = singularFromPlural(terminology.locationsLabel, generic.locationLabel)
  }

  const explicitIndustry = Boolean(industryType && industryType !== 'general_workforce')
  const label = (key: IndustryLabelKey) => terminology[terminologyKeyByLabel[key]]
  const pluralPhrase = (key: Extract<IndustryLabelKey, 'workers' | 'jobs' | 'locations' | 'shifts' | 'assignments'>, fallback: string) => (
    explicitIndustry ? label(key) : fallback
  )

  return {
    terminology,
    industryType: fallbackPreset.key,
    explicitIndustry,
    label,
    pluralPhrase,
  }
}

export const useIndustryTerminology = () => {
  const { accessContext } = useAccessContext()
  return useMemo(() => resolveIndustryTerminology(accessContext), [accessContext])
}
